<template>
  <DSFormField
    :id="id"
    #default="{ describedBy }"
    :name="props.name"
    :label="props.label ?? undefined"
    :inline-edit="props.inlineEdit"
    :hidden-label="props.hiddenLabel"
    :required="props.required"
    :disabled="props.disabled"
    :helper-text="props.helperText"
    :error-messages="errorMessages"
  >
    <DSFormFieldGroup :class="rootClasses">
      <DSBaseInput
        :id="id"
        ref="urlInputRef"
        v-model="modelValue"
        v-ds-tooltip="urlTooltip"
        :type="DS_BASE_INPUT_TYPE.URL"
        :name="props.name"
        :data-cy-suffix="props.name"
        :placeholder="props.placeholder"
        :inline-edit="props.inlineEdit"
        :disabled="props.disabled"
        :readonly="props.readonly"
        :required="props.required"
        :autocomplete="props.autocomplete"
        :maxlength="props.maxlength"
        :minlength="props.minlength"
        :clearable="isClearable"
        :has-error="hasErrors"
        :aria-label="props.ariaLabel"
        :aria-invalid="hasErrors ? 'true' : undefined"
        :aria-describedby="describedBy"
        @update:model-value="onInput"
        @clear="onClear"
        @focus="onFocus"
        @blur="onBlur"
      />

      <DSCopyButton
        v-if="props.showCopyAction"
        :value="copyValue"
        :class="DS_FORM_FIELD_CLASSES.InlineAction"
        :disabled="props.disabled"
        data-cy="ds-url-field__copy-button"
        variant="secondary"
        @mousedown="props.inlineEdit && $event.preventDefault()"
      />
    </DSFormFieldGroup>
  </DSFormField>
</template>

<script setup lang="ts">
import { ref, toRef } from "vue";
import DSCopyButton from "@/src/design_system/button_and_controls/copy_button/components/DSCopyButton.vue";
import DSBaseInput from "@/src/design_system/form_elements/base_input/components/DSBaseInput.vue";
import { DS_BASE_INPUT_TYPE } from "@/src/design_system/form_elements/base_input/types/inputType";
import DSFormField from "@/src/design_system/form_elements/form_field/components/DSFormField.vue";
import { DS_FORM_FIELD_CLASSES } from "@/src/design_system/form_elements/form_field/constants/classes";
import DSFormFieldGroup from "@/src/design_system/form_elements/form_field_group/components/DSFormFieldGroup.vue";
import { useDSFormFieldId } from "@/src/design_system/form_elements/shared/composables/useDSFormFieldId";
import { useDSInput } from "@/src/design_system/form_elements/shared/composables/useDSInput";
import { useDSUrlField } from "@/src/design_system/form_elements/url_field/composables/useDSUrlField";
import { useDSUrlFieldTooltip } from "@/src/design_system/form_elements/url_field/composables/useDSUrlFieldTooltip";
import type {
  DSUrlFieldEmits,
  DSUrlFieldModelValue,
  DSUrlFieldProps,
} from "@/src/design_system/form_elements/url_field/types";
import { vDsTooltip } from "@/src/design_system/primitives/tooltip/directives/vDsTooltip";

const props = withDefaults(defineProps<DSUrlFieldProps>(), {
  hiddenLabel: false,
  inlineEdit: false,
  disabled: false,
  readonly: false,
  required: false,
  clearable: true,
  showCopyAction: true,
  errorMessages: undefined,
  rules: undefined,
});

const modelValue = defineModel<DSUrlFieldModelValue>({ default: "" });
const emit = defineEmits<DSUrlFieldEmits>();

const urlInputRef = ref<InstanceType<typeof DSBaseInput>>();
const { focus, blur } = useDSInput(urlInputRef);

const { copyValue, errorMessages, hasErrors, isClearable, onClear, onInput, rootClasses } = useDSUrlField({
  props,
  modelValue,
});

const { id } = useDSFormFieldId({ props });

const { tooltip: urlTooltip } = useDSUrlFieldTooltip({
  inputRef: urlInputRef,
  value: modelValue,
  disabled: toRef(props, "disabled"),
});

const onFocus = (event: FocusEvent) => {
  emit("focus", event);
};

const onBlur = (event: FocusEvent) => {
  emit("blur", event);
};

defineExpose({
  focus,
  blur,
});
</script>
